import React, { useState } from "react";
import { X, Sliders, ChevronRight, Loader2 } from "lucide-react";
import { API_HOST } from "../constants";
import type { UserContextAnswers } from "../types";

export const USER_CONTEXT_QUESTIONS: {
  key: keyof UserContextAnswers | string;
  label: string;
  options: { value: string; label: string }[];
}[] = [
  {
    key: "experience",
    label: "Какой у вас опыт работы с ClickHouse?",
    options: [
      { value: "never", label: "Никогда не слышал / только начинаю" },
      { value: "reading", label: "Читал документацию, но не использовал" },
      { value: "queries", label: "Пишу запросы (SELECT, базовый SQL)" },
      { value: "config", label: "Настраиваю таблицы, движки, партиции" },
      { value: "admin", label: "Администрирую кластеры, репликацию, мониторинг" },
    ],
  },
  {
    key: "role",
    label: "Ваша роль?",
    options: [
      { value: "analyst", label: "Аналитик данных" },
      { value: "developer", label: "Разработчик (backend/data)" },
      { value: "student", label: "Студент" },
      { value: "dba", label: "DBA / администрирование БД" },
      { value: "other", label: "Другое" },
    ],
  },
  {
    key: "motivation",
    label: "Какая главная цель изучения?",
    options: [
      { value: "interview_prep", label: "Подготовка к собеседованию" },
      { value: "career_change", label: "Переквалификация / новая специальность" },
      { value: "upskill", label: "Повышение квалификации для текущей работы" },
      { value: "project", label: "Конкретный проект или задача" },
      { value: "curiosity", label: "Просто интересно" },
    ],
  },
  {
    key: "scale",
    label: "Масштаб данных?",
    options: [
      { value: "no_experience", label: "Нет опыта работы с данными" },
      { value: "local_test", label: "До 1 ГБ — локальные тесты / учёба" },
      { value: "analytics", label: "1 ГБ – 100 ГБ — аналитика, DWH" },
      { value: "production", label: "100 ГБ – 10 ТБ — продакшн кластер" },
      { value: "petabyte", label: "Петабайты — крупный кластер" },
    ],
  },
];

const QUESTIONS = USER_CONTEXT_QUESTIONS;

// Дополнительные поля из assessment для отображения в профиле
const _EXTRA_DISPLAY_KEYS: { key: string; label: string }[] = [
  { key: "summary", label: "Резюме оценки знаний" },
  { key: "known_concepts", label: "Известные концепции" },
  { key: "learning_concepts", label: "Концепции в процессе изучения" },
  { key: "recommended_course", label: "Рекомендованный курс" },
];

// Backward compat: decode old keys to new format
const _OLD_KEY_OPTIONS: Record<string, { value: string; label: string }[]> = {
  goal: [
    { value: "get_started", label: "Разобраться с нуля" },
    { value: "interview", label: "Подготовка к собеседованию" },
    { value: "optimize", label: "Оптимизация запросов в работе" },
    { value: "migration", label: "Миграция на ClickHouse" },
    { value: "other", label: "Другое" },
  ],
  workload: [
    { value: "learning_only", label: "Только учусь, без дедлайнов" },
    { value: "some_production", label: "Есть задачи в продакшене, но не срочно" },
    { value: "heavy", label: "Высокая нагрузка, нужны быстрые ответы" },
  ],
};

/** Возвращает список «вопрос — ответ» для отображения в профиле */
export function getUserContextDisplayRows(
  userContext: string | UserContextAnswers | null | undefined
): { question: string; answer: string }[] {
  if (!userContext) return [];
  let data: UserContextAnswers;
  try {
    data = typeof userContext === "string" ? JSON.parse(userContext) : userContext;
  } catch {
    return [];
  }
  if (!data || typeof data !== "object") return [];

  const rows: { question: string; answer: string }[] = [];

  // Editable quiz questions
  for (const q of USER_CONTEXT_QUESTIONS) {
    const val = data[q.key];
    if (val == null || val === "") continue;
    const strVal = String(val);
    const opt = q.options.find((o) => o.value === strVal);
    rows.push({ question: q.label, answer: opt?.label ?? strVal });
  }

  // Backward compat: old keys (goal, workload) not in new questions
  for (const [oldKey, options] of Object.entries(_OLD_KEY_OPTIONS)) {
    if (data[oldKey] != null && data[oldKey] !== "" && !USER_CONTEXT_QUESTIONS.some((q) => q.key === oldKey)) {
      const strVal = String(data[oldKey]);
      const opt = options.find((o) => o.value === strVal);
      rows.push({ question: oldKey === "goal" ? "Цель изучения" : "Нагрузка", answer: opt?.label ?? strVal });
    }
  }

  // Extra assessment fields
  for (const { key, label } of _EXTRA_DISPLAY_KEYS) {
    const val = data[key];
    if (val == null || val === "") continue;
    rows.push({ question: label, answer: String(val) });
  }

  return rows;
}

const STORAGE_KEY_DISMISSED = "user_context_modal_dismissed";

/** Один раз за сессию: уже показывали приглашение заполнить профиль (модалка) */
const SESSION_KEY_PROFILE_INVITE_SHOWN = "user_context_profile_invite_shown_v1";

export function setUserContextModalDismissed() {
  try {
    sessionStorage.setItem(STORAGE_KEY_DISMISSED, "1");
  } catch {}
}

export function wasUserContextModalDismissed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY_DISMISSED) === "1";
  } catch {
    return false;
  }
}

export function markProfileInviteShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_KEY_PROFILE_INVITE_SHOWN, "1");
  } catch {}
}

export function wasProfileInviteShownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY_PROFILE_INVITE_SHOWN) === "1";
  } catch {
    return false;
  }
}

interface UserContextSurveyProps {
  userId: number;
  initialAnswers?: UserContextAnswers | null;
  onSave?: (answers: UserContextAnswers) => void;
  onClose: () => void;
  /** Показать как модал при первом заходе (с кнопкой «Заполнить позже») */
  asOnboarding?: boolean;
  /** Заголовок (по умолчанию — «Настройки контекста для AI Tutor») */
  title?: string;
}

export function UserContextSurvey({
  userId,
  initialAnswers = null,
  onSave,
  onClose,
  asOnboarding = false,
  title = "Настройки контекста для AI Tutor",
}: UserContextSurveyProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<UserContextAnswers>(() => {
    if (initialAnswers && typeof initialAnswers === "object") return { ...initialAnswers };
    return {};
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = QUESTIONS[step];
  const currentValue = answers[current?.key as keyof UserContextAnswers];
  const isLast = step === QUESTIONS.length - 1;

  const handleNext = () => {
    if (isLast) {
      handleSubmit();
      return;
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_HOST}/user/${userId}/context`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || res.statusText);
      }
      onSave?.(answers);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleDismissLater = () => {
    if (asOnboarding) {
      setUserContextModalDismissed();
    }
    onClose();
  };

  const selectOption = (value: string) => {
    setAnswers((prev) => ({ ...prev, [current.key]: value }));
  };

  if (!current) return null;

  return (
    <div className="flex flex-col h-full max-h-[85vh] bg-white rounded-xl shadow-xl border border-neutral-border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sliders className="w-5 h-5 text-primary" />
          <h2 className="text-sm font-semibold text-neutral-text">{title}</h2>
        </div>
        <button
          type="button"
          onClick={asOnboarding ? handleDismissLater : onClose}
          className="p-1.5 rounded-lg hover:bg-neutral-light text-neutral-text/70 hover:text-neutral-text"
          aria-label={asOnboarding ? "Пройду позже" : "Закрыть"}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs text-neutral-text/70 mb-4">
          {asOnboarding
            ? "Несколько коротких ответов — и AI Tutor сможет подстраивать глубину и примеры под ваш уровень. Это займёт около минуты."
            : "Ответы помогают тьютору подстраивать объяснения под ваш опыт и цели. Необязательно, но желательно."}
        </p>
        <p className="text-sm font-medium text-neutral-text mb-3">{current.label}</p>
        <div className="space-y-2">
          {current.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => selectOption(opt.value)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                currentValue === opt.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-neutral-border bg-white hover:border-primary/40 hover:bg-neutral-light/50 text-neutral-text"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {error && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-neutral-border flex-shrink-0 bg-neutral-light/30">
        <div className="flex gap-1">
          {QUESTIONS.map((_, i) => (
            <span
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? "bg-primary" : i < step ? "bg-primary/50" : "bg-neutral-border"
              }`}
              aria-hidden
            />
          ))}
        </div>
        <div className="flex gap-2">
          {step > 0 && (
            <button
              type="button"
              onClick={handleBack}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-neutral-border bg-white text-neutral-text hover:bg-neutral-light"
            >
              Назад
            </button>
          )}
          <button
            type="button"
            onClick={handleDismissLater}
            className="px-3 py-2 text-sm font-medium rounded-lg border border-neutral-border bg-white text-neutral-text hover:bg-neutral-light"
          >
            {asOnboarding ? "Пройду позже" : "Отмена"}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={currentValue === undefined || currentValue === "" || saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isLast ? (
              "Сохранить"
            ) : (
              <>
                Далее
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
